/**
 * Color-coded multiselect for choosing which workflows appear in the trend
 * charts. Each workflow owns a stable color (shared with the line charts), so
 * this control doubles as the chart legend — no in-chart legend needed.
 *
 * @module WorkflowColorSelect
 * @see WorkflowSuiteTrendCharts
 */
import React from 'react';
import { cn } from '@chatsdet/ui';
import { stripBenchmarkPrefix } from './useWorkflowSuiteTransforms';

/** A workflow's stable colors: full-strength stroke/swatch + soft pill tint. */
export interface WorkflowColor {
  /** Solid color for the line stroke and active swatch. */
  solid: string;
  /** Translucent tint for the active pill background. */
  tint: string;
}

// Twelve distinct hues at a shared saturation/lightness so they read clearly on
// both light and dark surfaces. Indexed by workflow position so a fixed set of
// workflows keeps consistent colors. Past 12 the hues repeat, but each lap
// darkens (see workflowColorAt) so the 13th stays distinct from the 1st.
const PALETTE: readonly [number, number, number][] = [
  [217, 79, 53], // blue
  [142, 60, 42], // green
  [28, 88, 52], // orange
  [280, 55, 56], // purple
  [338, 72, 55], // pink
  [187, 68, 42], // teal
  [45, 88, 48], // amber
  [0, 74, 57], // red
  [255, 62, 60], // indigo
  [96, 52, 44], // olive-green
  [200, 80, 46], // azure
  [312, 58, 52], // magenta
];

/**
 * Color for the workflow at a given index in the sorted workflow list. Stable
 * for a fixed set of workflows; the hue repeats every 12 entries, with each lap
 * a step darker so repeated hues stay distinguishable. Lap 0 is unchanged.
 */
export function workflowColorAt(index: number): WorkflowColor {
  const [h, s, l] = PALETTE[index % PALETTE.length];
  const lap = Math.floor(index / PALETTE.length);
  const lightness = Math.max(20, l - lap * 10);
  return {
    solid: `hsl(${h}, ${s}%, ${lightness}%)`,
    tint: `hsla(${h}, ${s}%, ${lightness}%, 0.14)`,
  };
}

export interface WorkflowColorSelectProps {
  /** All workflow names available in range, sorted (drives color order). */
  workflows: string[];
  /** Stable color per workflow name. */
  colorMap: Map<string, WorkflowColor>;
  /** Currently selected names. Empty set is treated as "all selected". */
  selected: Set<string>;
  /** True when every workflow is implicitly active (no explicit selection). */
  allSelected: boolean;
  onToggle: (name: string) => void;
  onSelectAll: () => void;
}

/**
 * Wrapping row of toggle pills, one per workflow, each tinted with the color
 * that workflow uses across the charts. Active pills are filled with their
 * tint; inactive pills keep a hollow color ring so their identity stays visible.
 */
export function WorkflowColorSelect({
  workflows,
  colorMap,
  selected,
  allSelected,
  onToggle,
  onSelectAll,
}: WorkflowColorSelectProps) {
  const activeCount = allSelected ? workflows.length : selected.size;

  return (
    <div className="space-y-2" role="group" aria-label="Filter workflows">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Workflows</span>
        <span className="text-xs text-muted-foreground/70">
          {activeCount} of {workflows.length}
        </span>
        {!allSelected && (
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:underline"
          >
            Select all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {workflows.map((name) => {
          const color = colorMap.get(name);
          if (!color) return null;
          const isActive = allSelected || selected.has(name);
          return (
            <button
              key={name}
              type="button"
              aria-pressed={isActive}
              title={name}
              onClick={() => onToggle(name)}
              className={cn(
                'inline-flex max-w-[260px] items-center gap-2 rounded-full border px-3 py-1.5',
                'text-xs font-medium transition-all duration-150 focus-visible:outline-none',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                isActive
                  ? 'text-foreground shadow-sm'
                  : 'border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-foreground/30',
              )}
              style={
                isActive
                  ? { backgroundColor: color.tint, borderColor: color.solid }
                  : undefined
              }
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={
                  isActive
                    ? { backgroundColor: color.solid }
                    : { boxShadow: `inset 0 0 0 1.5px ${color.solid}` }
                }
              />
              <span className="truncate">{stripBenchmarkPrefix(name)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
